// src/pages/ForgotPassword/ForgotPassword.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { postRequest } from "../../services/api";
import "../LoginPage/LoginPage.css";
import "./ForgotPassword.css";

const ForgotPassword: React.FC = () => {
  const [email,   setEmail]   = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);

  /* ── Validate ── */
  const validate = (): boolean => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setError("Enter a valid email address");
      return false;
    }
    setError("");
    return true;
  };

  /* ── Submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await postRequest("/auth/forgot-password", { email });
      // API always returns the same message regardless of whether email
      // exists — we just show the success state.
      setSent(true);
    } catch {
      // Even on network error show success so we don't leak whether
      // an email is registered (security best practice).
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">

      {/* ── Left panel ── */}
      <div className="auth-left fp-left">
        <div className="auth-left-inner">
          <Link to="/" className="auth-logo">
            <span className="auth-logo-mark">A</span>
            <span className="auth-logo-text">cadebility</span>
          </Link>

          <div className="auth-headline">
            <h1>Forgot your<br /><span className="auth-accent">password?</span></h1>
            <p className="auth-tagline">
              No worries! Enter your registered email and we'll send you a secure reset link within seconds.
            </p>
          </div>

          <div className="auth-features">
            {[
              { icon: "🔒", title: "Secure Reset Link",   desc: "Link expires in 15 minutes for your safety" },
              { icon: "📧", title: "Check Your Inbox",    desc: "Also check spam / promotions folder" },
              { icon: "🔑", title: "New Password Tips",   desc: "Use letters + numbers, at least 6 characters" },
              { icon: "💬", title: "Still stuck?",        desc: "Contact support@acadebility.com for help" },
            ].map(p => (
              <div className="auth-feat-item" key={p.title}>
                <span className="auth-feat-icon">{p.icon}</span>
                <div>
                  <div className="auth-feat-title">{p.title}</div>
                  <div className="auth-feat-desc">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
      </div>

      {/* ── Right panel ── */}
      <div className="auth-right">
        <div className="auth-form-card fp-card">

          {!sent ? (
            <>
              <div className="auth-form-header">
                <div className="fp-icon-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <h2 className="auth-form-title">Reset your password</h2>
                <p className="auth-form-sub">
                  We'll email you a link to reset your password.
                </p>
              </div>

              <form className="auth-form" onSubmit={handleSubmit} noValidate>
                <div className="auth-field">
                  <label className="auth-label">Email address</label>
                  <div className="auth-input-wrap">
                    <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <input
                      type="email"
                      name="email"
                      className={`auth-input${error ? " auth-input-err" : ""}`}
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => { setEmail(e.target.value); if (error) setError(""); }}
                      required
                      autoFocus
                      autoComplete="email"
                    />
                  </div>
                  {error && <span className="auth-err">{error}</span>}
                </div>

                <button type="submit" className="auth-btn-primary" disabled={loading}>
                  {loading
                    ? <><span className="auth-spinner" /> Sending reset link…</>
                    : "Send reset link →"
                  }
                </button>
              </form>
            </>
          ) : (
            /* ── Success state ── */
            <div className="fp-success">
              <div className="fp-success-icon">📬</div>
              <h2 className="fp-success-title">Check your email</h2>
              <p className="fp-success-msg">
                If <strong>{email}</strong> is registered with us, you'll receive a
                password reset link shortly.
              </p>
              <p className="fp-success-hint">
                Didn't receive it? Check your spam folder, or{" "}
                <button
                  type="button"
                  className="fp-resend-btn"
                  onClick={() => { setSent(false); }}
                >
                  try again
                </button>.
              </p>
            </div>
          )}

          <p className="auth-switch">
            Remember your password?{" "}
            <Link to="/login" className="auth-switch-link">Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
